// A short burst of confetti from the top of the screen when everything is done.
import { useEffect, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { prefersReducedMotion } from '../lib/haptics';

const COLORS = ['#4d74ff', '#26b0f0', '#34cf88', '#f9b72a', '#f5577a', '#9a7cff', '#ffffff'];

interface Piece {
  id: number;
  x: number;
  drift: number;
  rotate: number;
  delay: number;
  duration: number;
  color: string;
  shape: 'rect' | 'circle';
}

export function Confetti({ burst }: { burst: number }) {
  const [pieces, setPieces] = useState<Piece[]>([]);

  useEffect(() => {
    if (burst === 0 || prefersReducedMotion()) return;
    const list = Array.from({ length: 70 }, (_, i): Piece => ({
      id: burst * 1000 + i,
      x: Math.random() * 100,
      drift: (Math.random() - 0.5) * 30,
      rotate: (Math.random() - 0.5) * 900,
      delay: Math.random() * 0.25,
      duration: 1.6 + Math.random() * 1.2,
      color: COLORS[i % COLORS.length]!,
      shape: Math.random() > 0.35 ? 'rect' : 'circle',
    }));
    setPieces(list);
    const timer = window.setTimeout(() => setPieces([]), 3200);
    return () => window.clearTimeout(timer);
  }, [burst]);

  if (pieces.length === 0) return null;
  return createPortal(
    <div className="confetti" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className={`confetti__piece confetti__piece--${p.shape}`}
          style={
            {
              left: `${p.x}%`,
              background: p.color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              '--drift': `${p.drift}vw`,
              '--rotate': `${p.rotate}deg`,
            } as CSSProperties
          }
        />
      ))}
    </div>,
    document.body,
  );
}
